/**
 * Textes de l'orchestration (2026-10-10, branche `complet/orchestration`) : reprises d'étape et
 * tentatives d'un run de workflow, politique de rattrapage d'un cron, backfills, déclencheurs sur
 * événement. Branchés dans `fr.ts` sous `orchestration` ; `orchestration.en.ts` porte exactement les
 * mêmes clés (typage + `i18n.test.ts`).
 */
export const orchestrationFr = {
  schedule: {
    infrastructure: 'Infrastructure des runs planifiés',
    infrastructureHelp:
      'Un run planifié démarre seul : il lui faut l’infrastructure qu’un clic sur « Exécuter » aurait demandée.',
    timezone: 'Fuseau horaire',
    timezoneHelp: 'Fuseau IANA de l’expression cron (Europe/Paris par défaut).',
    catchUp: 'Échéances manquées',
    catchUpHelp:
      'Ce que fait le planificateur des échéances passées pendant un arrêt (redémarrage, changement de leader).',
    catchUpPolicies: {
      none: 'Les ignorer',
      last: 'Rejouer la plus récente (défaut)',
      all: 'Les rejouer toutes',
    },
    catchUpShort: {
      none: 'rattrapage : aucun',
      last: 'rattrapage : la dernière',
      all: 'rattrapage : toutes',
    },
    noInfrastructure: 'Aucune infrastructure',
  },
  retries: {
    retries: 'Reprises après échec',
    retriesHelp: '0 à 10 nouvelles tentatives ; chacune est un nouveau run du job (GRAAL_ATTEMPT).',
    delay: 'Délai avant reprise (s)',
    delayHelp: '0 à 86 400 secondes, 10 par défaut.',
    column: 'Reprises',
    summary_one: '{{count}} reprise · {{delay}} s',
    summary_other: '{{count}} reprises · {{delay}} s',
    none: 'aucune',
    invalidRetries: 'Entre 0 et 10.',
    invalidDelay: 'Entre 0 et 86 400 secondes.',
    taskRetries: 'Reprises — {{task}}',
    taskDelay: 'Délai (s) — {{task}}',
  },
  attempts: {
    title: 'Tentatives des étapes',
    description:
      'Une ligne par tentative : une étape reprise après un échec en a plusieurs, chacune avec son run de job.',
    step: 'Étape',
    attempt: 'Tentative',
    status: 'Statut',
    jobRun: 'Run du job',
    error: 'Erreur',
    started: 'Début',
    openJobRun: 'Ouvrir le run',
    empty: 'Aucune tentative pour l’instant',
    emptyHelp: 'Les tentatives apparaissent dès que le moteur lance la première étape.',
    statuses: {
      starting: 'Démarrage',
      running: 'En cours',
      succeeded: 'Réussie',
      failed: 'Échouée',
    },
  },
  backfills: {
    tab: 'Backfills',
    title: 'Backfills',
    description:
      'Rejouer la planification cron sur un intervalle passé : un run par échéance, chacun avec sa date logique (GRAAL_LOGICAL_DATE).',
    notCron: 'Pas de backfill sans planification cron',
    notCronHelp:
      'Un backfill rejoue les échéances d’une expression cron. Planifiez d’abord cette ressource (onglet Définition ou Résumé).',
    create: 'Nouveau backfill',
    createTitle: 'Rejouer un intervalle passé',
    createDescription:
      'Un run par échéance de « {{cron}} » entre le début et la fin, lancés au plus N à la fois, comme un run manuel (quotas compris).',
    start: 'Début',
    end: 'Fin',
    dateHelp: 'Date ISO 8601, par exemple 2026-10-01 ou 2026-10-01T06:00:00Z.',
    maxParallel: 'Runs simultanés',
    maxParallelHelp: 'De 1 à 5.',
    infrastructure: 'Infrastructure',
    infrastructureHelp: 'Par défaut, celle de la planification cron.',
    defaultInfrastructure: 'Celle de la planification',
    submit: 'Lancer le backfill',
    created: 'Backfill lancé : {{total}} échéances.',
    createFailed: 'Le backfill n’a pas pu être lancé.',
    cancel: 'Annuler',
    cancelled: 'Backfill annulé : les échéances en attente ne partiront pas.',
    cancelFailed: 'Le backfill n’a pas pu être annulé.',
    invalidDate: 'Date invalide.',
    endInFuture: 'La fin ne peut pas être dans le futur.',
    endBeforeStart: 'La fin doit suivre le début.',
    invalidParallel: 'Entre 1 et 5.',
    columns: {
      period: 'Intervalle',
      status: 'Statut',
      progress: 'Avancement',
      parallel: 'Simultanés',
      createdBy: 'Lancé par',
      created: 'Créé',
    },
    progress: '{{done}} / {{total}} terminés',
    progressDetail:
      '{{succeeded}} réussis · {{failed}} échoués · {{launched}} en cours · {{pending}} en attente',
    statuses: {
      running: 'En cours',
      complete: 'Terminé',
      cancelled: 'Annulé',
    },
    empty: 'Aucun backfill',
    emptyHelp: 'Rejouez une période manquée : un run par échéance du cron.',
  },
  triggers: {
    title: 'Déclencheurs',
    subtitle:
      'Lancer un job ou un workflow de {{project}} quand un événement arrive : la fin d’un run, un objet déposé dans un bucket, un appel signé.',
    // Variante sans nom (contexte i18next `unnamed`) tant que le nom du projet n'est pas lu : jamais d'identifiant dans la phrase.
    subtitle_unnamed:
      'Lancer un job ou un workflow de ce projet quand un événement arrive : la fin d’un run, un objet déposé dans un bucket, un appel signé.',
    create: 'Nouveau déclencheur',
    createTitle: 'Nouveau déclencheur',
    createSubtitle: 'Un événement lance au plus un run de la cible.',
    created: 'Déclencheur {{name}} créé.',
    deleted: 'Déclencheur {{name}} supprimé.',
    deleteFailed: 'Le déclencheur n’a pas pu être supprimé.',
    delete: 'Supprimer le déclencheur',
    enabled: 'Activé',
    disabled: 'Désactivé',
    enable: 'Activer',
    disable: 'Désactiver',
    toggled: 'Déclencheur {{name}} : {{state}}.',
    toggleFailed: 'Le déclencheur n’a pas pu être modifié.',
    empty: 'Aucun déclencheur',
    emptyHelp: 'Enchaînez vos traitements sur un événement plutôt qu’à heure fixe.',
    types: {
      run_completed: 'Fin d’un run',
      object_created: 'Objet déposé',
      webhook: 'Webhook signé',
    },
    typeHelp: {
      run_completed: 'La fin d’un run d’un job ou d’un workflow du projet, selon son statut.',
      object_created:
        'Un objet créé sous un préfixe d’un bucket. Ceux déjà présents à la création ne déclenchent pas.',
      webhook: 'Un appel HTTP signé (HMAC-SHA256) d’un système extérieur.',
    },
    kinds: { job: 'Job', workflow: 'Workflow', bucket: 'Bucket' },
    endStatuses: { complete: 'Réussi', failed: 'Échoué', killed: 'Arrêté' },
    fields: {
      name: 'Nom',
      description: 'Description',
      type: 'Événement',
      targetKind: 'Cible',
      targetJob: 'Job lancé',
      targetWorkflow: 'Workflow lancé',
      infrastructure: 'Infrastructure',
      infrastructureHelp: 'Où tourne la cible ; par défaut, celle de sa planification cron.',
      sourceKind: 'Source',
      sourceJob: 'Job observé',
      sourceWorkflow: 'Workflow observé',
      sourceBucket: 'Bucket observé',
      prefix: 'Préfixe des clés',
      prefixHelp: 'Par exemple « entrants/ ». Vide : tout le bucket.',
      onComplete: 'Quand le run réussit',
      onFailed: 'Quand le run échoue',
      onKilled: 'Quand le run est arrêté',
      enabled: 'Activé dès la création',
    },
    atLeastOneStatus: 'Choisissez au moins un statut de fin.',
    sameAsTarget: 'La source ne peut pas être la cible elle-même.',
    columns: {
      name: 'Nom',
      type: 'Événement',
      target: 'Cible',
      source: 'Source',
      state: 'État',
      lastFired: 'Dernier déclenchement',
    },
    never: 'jamais',
    detail: {
      tabs: { summary: 'Résumé', events: 'Événements', webhook: 'Webhook' },
      type: 'Événement',
      target: 'Cible',
      source: 'Source',
      statuses: 'Statuts de fin',
      prefix: 'Préfixe',
      infrastructure: 'Infrastructure',
      owner: 'Créé par',
      created: 'Créé',
      lastFired: 'Dernier déclenchement',
      openTarget: 'Ouvrir la cible',
    },
    events: {
      title: 'Journal des événements',
      description: 'Les derniers événements vus, du plus récent au plus ancien. Un doublon ne relance rien.',
      key: 'Clé',
      status: 'Statut',
      run: 'Run lancé',
      detail: 'Détail',
      at: 'Reçu',
      openRun: 'Ouvrir le run',
      empty: 'Aucun événement reçu',
      emptyHelp: 'Les événements apparaissent ici dès que la source en produit.',
      statuses: {
        pending: 'En attente',
        fired: 'Run lancé',
        failed: 'Échec',
        duplicate: 'Doublon',
      },
    },
    webhook: {
      title: 'Appeler le webhook',
      description:
        'Aucun jeton : la requête est authentifiée par sa signature. L’horodatage doit être à moins de 5 minutes de l’horloge du serveur ; tout refus rend le même 401.',
      url: 'URL du webhook',
      signature: 'Signature',
      signatureHelp:
        'X-Graal-Signature: sha256=<hex> = HMAC-SHA256(secret, horodatage + "." + corps), X-Graal-Timestamp en secondes Unix, X-Graal-Delivery (facultatif) déduplique.',
      example: 'Exemple (bash) — le secret est lu au clavier, jamais écrit dans la commande',
      rotate: 'Renouveler le secret',
      rotateHint: 'L’ancien secret cesse aussitôt de signer : mettez à jour l’appelant.',
      rotated: 'Nouveau secret émis.',
      rotateFailed: 'Le secret n’a pas pu être renouvelé.',
      secret: 'Secret de signature',
      copySecret: 'Copier le secret',
      once: 'Affiché une seule fois : il ne sera plus jamais relu. Conservez-le dans votre coffre.',
      noSecret: 'Le serveur n’a pas rendu de secret : renouvelez-le.',
      notWebhook: 'Ce déclencheur n’est pas un webhook.',
      done: 'J’ai enregistré le secret',
      revealTitle: 'Webhook {{name}} prêt',
    },
  },
} as const;
